import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import {
    IQueryContextChange,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    QueryContextChange,
    QueryContextService,
} from 'src/app/services/query-context/query-context.service';
import { selectedValuesPill } from 'src/app/utils/ng-zorro';

type MultiSelectSettingOption = {
    value: any;
    label: string;
};

export type OptionChange =
    | { changeType: 'add' | 'remove'; value: any }
    | undefined;

// TODO flatten
export interface IMultiSelectSettingDashboardItem {
    options: {
        id: QueryContextSettingID;
        viewtype: 'multiSelect';
        displayName: string;
        /** Current value */
        value: any[];
        /** Possible values */
        selectOptions: Array<MultiSelectSettingOption>;
    };
}

const ALL_VALUES_OPTION: MultiSelectSettingOption = {
    value: 'all',
    label: 'All',
};

@Component({
    selector: 'app-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
})
export class MultiSelectComponent {
    @Input()
    option!: IMultiSelectSettingDashboardItem['options'];
    @Input()
    nzMaxTagCount!: number;
    @Output()
    change = new EventEmitter<IQueryContextChange>();

    // Available options
    availableOptions!: MultiSelectSettingOption[];
    // Selected option values
    selectedOptionValues!: MultiSelectSettingOption['value'][];

    // View model for the available options (added 'all' option)
    availableOptionsViewModel!: MultiSelectSettingOption[];
    // View model for the selected option values (added 'all' option)
    selectedOptionValuesViewModel: MultiSelectSettingOption['value'][] = [];

    // Previous selected option values view model
    previousSelectedOptionValuesViewModel: MultiSelectSettingOption['value'][] =
        [];
    // Are all values selected
    allValuesSelected: boolean = false;

    constructor(
        private queryContextService: QueryContextService,
        private cd: ChangeDetectorRef
    ) {
        // this.formControl.valueChanges.subscribe(observer => {
        //     this.onChange();
        // });
    }

    ngOnInit(): void {
        // if nzMaxTagCount is not set, set it to 100
        this.nzMaxTagCount = this.nzMaxTagCount ?? 100;
        // this.formControl.setValue(this.option.value, {emitEvent: false});
        this.queryContextService.appliedQueryContextEmitter.subscribe(
            this.queryContextSubscriber
        );

        this.availableOptions = this.option.selectOptions.slice();
        this.availableOptionsViewModel = [
            ALL_VALUES_OPTION,
            ...this.availableOptions,
        ];

        // initialize selectedOptionValues, if some values are not in availableOptions, throw error
        const selectedOptionValuesNotInAvailableOptions =
            this.selectedOptionValues?.find(
                (selectedOptionValue) =>
                    !this.option.selectOptions.find(
                        (option) => option.value === selectedOptionValue
                    )
            ) ?? null;
        if (selectedOptionValuesNotInAvailableOptions) {
            throw new Error(
                `selected option value ${selectedOptionValuesNotInAvailableOptions} is not in available options`
            );
        }
        this.selectedOptionValues = this.option.value.slice();

        // initialize selectedOptionValuesViewModel, if selected all availableOptions, add to selectedOptionValuesViewModel ALL_VALUES_OPTION.value
        const { selectedOptionValuesViewModel, allValuesSelected } =
            this.getViewOptionsValuesModel(
                this.selectedOptionValues,
                this.option.selectOptions
            );
        this.selectedOptionValuesViewModel =
            selectedOptionValuesViewModel.slice();
        this.allValuesSelected = allValuesSelected;

        this.previousSelectedOptionValuesViewModel =
            this.selectedOptionValuesViewModel.slice();
    }

    onChange(value: any[]) {
        // is all values selected
        this.allValuesSelected = value.includes(ALL_VALUES_OPTION.value);
        const selectedOptions = structuredClone(
            // value.sort((a, b) => {
            //     // are a and b numbers?
            //     if (typeof a === 'number' && typeof b === 'number') {
            //         return a - b;
            //     }
            //     return a.localeCompare(b);
            // })
            // sort by order of this.option.selectOptions
            this.option.selectOptions
                .filter(
                    (option: any) =>
                        this.allValuesSelected || value.includes(option.value)
                )
                .map((option: any) => option.value)
        );
        // this.selectedOptions = value.sort();
        this.selectedOptionValues = selectedOptions.map(
            (option: any) => option.value
        );
        console.log({
            msg: 'MultiSelectComponent.onChange',
            id: this.option.id,
            value: this.selectedOptionValues,
        });
        this.queryContextService.changePendingQueryContext({
            setting: { id: this.option.id, value: this.selectedOptionValues },
        });
    }

    queryContextSubscriber = (contextChange: QueryContextChange) => {
        if (contextChange.reason === 'InitialContext') {
            // if option id in contextChange.context.settings
            // then set value from contextChange.context.settings
            const setting: MultiSelectSettingOption['value'][] = contextChange
                .context.settings[
                this.option.id
            ] as MultiSelectSettingOption['value'][];
            if (setting) {
                // if setting item is not in this.availableOptions, throw error
                const settingItemNotInAvailableOptions = setting.find(
                    (settingItem) =>
                        !this.option.selectOptions.find(
                            (option) => option.value === settingItem
                        )
                );
                if (settingItemNotInAvailableOptions) {
                    throw new Error(
                        `setting item ${settingItemNotInAvailableOptions} is not in available options`
                    );
                }

                this.selectedOptionValues = setting as any;
                const { selectedOptionValuesViewModel, allValuesSelected } =
                    this.getViewOptionsValuesModel(
                        this.selectedOptionValues,
                        this.option.selectOptions
                    );
                this.selectedOptionValuesViewModel =
                    selectedOptionValuesViewModel;
                this.allValuesSelected = allValuesSelected;
                this.cd.detectChanges();
            }
        }
    };

    /**
     * Generates a view model for the selected options in a multi-select setting.
     *
     * @param selectedOptionValues - An array of the currently selected option values.
     * @param availableOptions - An array of all available options.
     *
     * @returns An object containing the view model for the selected option values and a boolean indicating whether all options are selected.
     *
     * @example
     *
     * const selectedOptionValues = ['option1', 'option2'];
     * const availableOptions = [
     *   { value: 'option1', label: 'Option 1' },
     *   { value: 'option2', label: 'Option 2' },
     *   { value: 'option3', label: 'Option 3' }
     * ];
     *
     * const result = getViewOptionsValuesModel(selectedOptionValues, availableOptions);
     *
     * // Expected result:
     * // {
     * //   selectedOptionValuesViewModel: ['option1', 'option2'],
     * //   allValuesSelected: false
     * // }
     */
    getViewOptionsValuesModel(
        selectedOptionValues: MultiSelectSettingOption['value'][],
        availableOptions: MultiSelectSettingOption[]
    ) {
        const allValuesSelected =
            selectedOptionValues.length === availableOptions.length;
        const selectedOptionValuesViewModel = [
            ...(allValuesSelected
                ? [ALL_VALUES_OPTION.value, ...selectedOptionValues]
                : selectedOptionValues),
        ];
        return {
            selectedOptionValuesViewModel,
            allValuesSelected,
        };
    }

    public selectedValuesPill() {
        return selectedValuesPill(
            this.availableOptions
                .filter((option) =>
                    this.selectedOptionValues.includes(option.value)
                )
                .map((option) => option.label),
            this.availableOptions.map((option: any) => option.label)
        );
    }

    /**
     * Detects whether an option has been added or removed based on the previous and current options.
     * @param current - The current set of selected options.
     * @param previous - The old set of selected options.
     * @returns An object representing the change type and value, or undefined if no change is detected.
     */
    private detectChange(current: any[], previous: any[]): OptionChange {
        const hasAdded = current.length > previous.length;
        const hasRemoved = current.length < previous.length;

        if (hasAdded) {
            // Find the added option
            const added = current.find((option) => !previous.includes(option));
            return { changeType: 'add', value: added };
        } else if (hasRemoved) {
            // Find the removed option
            const removed = previous.find(
                (option) => !current.includes(option)
            );
            return { changeType: 'remove', value: removed };
        }

        return undefined;
    }

    onSelectedOptionsChange() {
        this.queryContextService.changePendingQueryContext({
            setting: { id: this.option.id, value: this.selectedOptionValues },
        });
    }

    /**
     * Clears all selection view states. Notifies of the change.
     */
    private clearSelection(): void {
        this.allValuesSelected = false;
        this.selectedOptionValues = [];
        this.selectedOptionValuesViewModel = [];
        this.previousSelectedOptionValuesViewModel = [];
        this.onSelectedOptionsChange();
    }

    /**
     * Selects all available options. Notifies of the change.
     */
    private allSelection(): void {
        this.allValuesSelected = true;
        this.selectedOptionValues = this.availableOptions.map((o) => o.value);
        this.selectedOptionValuesViewModel =
            this.previousSelectedOptionValuesViewModel = [
                ALL_VALUES_OPTION.value,
                ...this.availableOptions.map((o) => o.value),
            ];
        this.onSelectedOptionsChange();
    }

    /**
     * Handles the special case where the 'All' option is selected or deselected.
     * @param change - The change detected in the options.
     * @returns A boolean indicating whether the 'All' option change was handled.
     */
    private handleAllOptionChange(change: OptionChange): boolean {
        if (change === undefined) {
            throw new Error('Change is undefined');
        }
        if (change.value !== ALL_VALUES_OPTION.value) {
            return false;
        }

        // Set the allValuesSelected flag based on the change type
        this.allValuesSelected = change.changeType === 'add';
        if (this.allValuesSelected) {
            this.allSelection();
        } else {
            this.clearSelection();
        }

        return true;
    }

    /**
     * Handles changes in the selected view options.
     * This method is invoked whenever the selected options change.
     * It updates the internal state to reflect the current selection,
     * and emits an event to notify other parts of the system about the change.
     * @param options - The current list of selected options.
     */
    onSelectedViewOptionsModelChange(options: any[]): void {
        if (options.length === 0) {
            this.clearSelection();
            return;
        }

        const change = this.detectChange(
            options,
            this.selectedOptionValuesViewModel
        );
        if (!change) {
            throw new Error('Change detection failed');
        }

        this.previousSelectedOptionValuesViewModel = options.slice();

        // Handle the special case where the 'all' option is selected or deselected
        if (this.handleAllOptionChange(change)) {
            // The 'all' option change was handled, no need to continue
            return;
        }

        switch (change.changeType) {
            case 'add':
                let selectedOptionValuesViewModel = options.slice();

                this.selectedOptionValues.push(change.value);

                this.allValuesSelected = this.checkAllValuesSelected(
                    this.selectedOptionValues,
                    this.availableOptions
                );
                if (this.allValuesSelected) {
                    this.allSelection();
                    return;
                }
                this.selectedOptionValuesViewModel =
                    this.previousSelectedOptionValuesViewModel =
                        this.selectedOptionValues.slice();
                break;
            case 'remove':
                // remove 'all' and change.value from selectedOptionValues
                let selectedOptionValues = options.slice();
                selectedOptionValues = selectedOptionValues.filter(
                    (option) => option !== ALL_VALUES_OPTION.value
                );
                selectedOptionValues = selectedOptionValues.filter(
                    (option) => option !== change.value
                );

                this.allValuesSelected = false;
                this.selectedOptionValues = selectedOptionValues.slice();
                this.selectedOptionValuesViewModel =
                    selectedOptionValues.slice();
                this.previousSelectedOptionValuesViewModel =
                    selectedOptionValues.slice();

                break;
        }

        this.onSelectedOptionsChange();
    }

    checkAllValuesSelected(selected: any[], available: any[]) {
        return selected.length === available.length;
    }
}
